import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cta, industries } from '../content';
import { usePageMeta, Swipe, Button, Arrow, SmartLink } from '../ui';

type Dest = { t: string; d: string; h: string; k: string };

const DESTS: Dest[] = [
  { t: 'Book 30 minutes', d: 'Bring one product.', h: '/book-a-demo', k: 'demo call meeting book contact' },
  { t: 'How it works', d: 'Tag, hand over, own. From the factory to every owner.', h: '/platform', k: 'platform product features registration warranty' },
  { t: 'Interactive tour', d: 'Click through the owner, dealer and brand views.', h: '/tour', k: 'tour demo example scan' },
  { t: 'Industries', d: 'Eight sectors, in their own terms.', h: '/industries', k: 'industries sectors' },
  ...industries.map((i) => ({ t: i.name, d: i.short, h: `/industries/${i.slug}`, k: `industry sector ${i.name} ${i.short}` })),
  { t: 'ERP integrations', d: 'Native Epicor Kinetic connector. Your ERP stays master.', h: '/integrations', k: 'erp epicor kinetic ifs sap integration api' },
  { t: 'Pricing', d: 'Start with one product line.', h: '/pricing', k: 'pricing price cost plans' },
  { t: 'Why trust us', d: 'Your QR codes will outlive any software contract.', h: '/why-trust-us', k: 'trust security legal exit gdpr hosting' },
  { t: 'Customer story', d: 'A leading European manufacturer, packing slips first.', h: '/customers', k: 'customer story case study' },
  { t: 'Digital Product Passport', d: 'Same code, two jobs.', h: '/platform/digital-product-passport', k: 'dpp passport espr compliance' },
  { t: 'Trust Centre', d: 'Security, data protection, sub-processors.', h: '/trust/', k: 'trust centre security' },
  { t: 'Legal hub', d: 'Terms, DPA, SLA, AI Statement.', h: '/legal/', k: 'legal terms dpa sla' },
  { t: 'Blog', d: 'Field notes on aftersales, dealers and DPP.', h: '/blog/', k: 'blog articles posts' },
];

export default function NotFound({ hint }: { hint?: 'industry' }) {
  usePageMeta('Page not found');
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Empty search: four quiet starting points. Booking is already the page's one button.
    if (!s) return DESTS.filter((d) => ['/platform', '/industries', '/pricing', '/why-trust-us'].includes(d.h));
    return DESTS.filter((d) => `${d.t} ${d.d} ${d.k}`.toLowerCase().includes(s));
  }, [q]);

  return (
    <div className="nf">
      <section className="wrap pb-20 pt-14 sm:pt-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div className="min-w-0">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-bm-alt px-3.5 py-1.5 font-mono text-[12px] text-bm-muted ring-1 ring-bm-line">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4A045]" />
              <span className="truncate">404: no record found for {pathname}</span>
            </div>
            <h1 className="t-h1 nf-h1 mt-8 max-w-[15ch]">
              We could not find that <Swipe draw>page.</Swipe>
            </h1>
            <p className="t-lede mt-6 max-w-[46ch]">
              {hint === 'industry'
                ? 'We have no page for that sector yet, so try one below.'
                : 'The link may be old, or mistyped.'}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button href={`${cta.primaryHref}?src=404`}>
                {cta.primary} <Arrow />
              </Button>
              <SmartLink href="/" className="py-2 text-[15px] font-medium sm:px-2 text-bm-muted hover:text-bm-indigo">
                or go to the homepage
              </SmartLink>
            </div>
          </div>

          <div className="min-w-0 rounded-[22px] bg-bm-alt p-5 sm:p-7">
            <label htmlFor="nf-q" className="text-[13px] font-medium text-bm-muted">
              Find what you were looking for
            </label>
            <div className="mt-2 flex items-center gap-2 rounded-xl bg-white px-4 ring-1 ring-bm-line focus-within:ring-2 focus-within:ring-bm-mid">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" stroke="#67648F" strokeWidth="1.7" />
                <path d="m13.2 13.2 3.3 3.3" stroke="#67648F" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <input
                id="nf-q"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Try “pricing”, “dealers” or “Epicor”"
                className="h-12 w-full bg-transparent text-[16px] text-bm-ink outline-none placeholder:text-bm-muted/70"
              />
            </div>
            <ul className="mt-3 divide-y divide-bm-line" aria-live="polite">
              {shown.slice(0, 6).map((d) => (
                <li key={d.h}>
                  <SmartLink href={d.h} className="group flex items-center justify-between gap-4 rounded-lg px-1 py-3.5">
                    <span className="min-w-0">
                      <span className="block text-[16px] font-semibold text-bm-indigo group-hover:text-bm-emerald">{d.t}</span>
                      <span className="block truncate text-[14px] text-bm-muted">{d.d}</span>
                    </span>
                    <span className="shrink-0 text-bm-muted transition-transform duration-200 group-hover:translate-x-1 group-hover:text-bm-emerald">
                      <Arrow />
                    </span>
                  </SmartLink>
                </li>
              ))}
              {shown.length === 0 && (
                <li className="px-1 py-5 text-[15px] text-bm-muted">
                  Nothing matches that. Ask us directly on a{' '}
                  <SmartLink href={cta.primaryHref} className="font-semibold text-bm-emerald underline underline-offset-2">
                    30-minute call
                  </SmartLink>
                  .
                </li>
              )}
            </ul>
          </div>
        </div>

        {hint === 'industry' && (
          <div className="mt-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((i) => (
              <SmartLink key={i.slug} href={`/industries/${i.slug}`} className="rounded-2xl p-5 ring-1 ring-bm-line hover:ring-bm-indigo">
                <span className="block text-[17px] font-bold text-bm-indigo">{i.name}</span>
                <span className="mt-1 block text-[14.5px] text-bm-muted">{i.short}</span>
              </SmartLink>
            ))}
          </div>
        )}
      </section>
      <style>{`.v2 .nf-h1 { font-size: clamp(38px, 4.8vw, 64px); line-height: 1.02; }`}</style>
    </div>
  );
}
